import { Unit } from '../unit';
import {
  BooleanType,
  DateType,
  DatetimeType,
  NullableType,
  NumberType,
  QuantityType,
  StringType,
} from './expr_types';

export type RowStructType = {
  ty: 'struct';
  fields: Map<string, RowExprType>;
};

// In a tabular data source it's common for a quantity to be stored across two
// columns: one for the value, and one for the unit. The Row* expression type
// variants help with this use case: they contain enough additional information
// that we can resolve quantities in those contexts.
export type RowQuantityType = QuantityType & {
  // Relative to the appropriate enclosing input or reference data
  column: string;

  // A map from an arbitrary string used in this dataset to the unit it
  // represents. You can think of this map as being a way to parse each string
  // as a unit. In practice, the strings won't be arbitrary, but will represent
  // a serialization of either a pipeline v2 unit or an LScript unit; this map
  // is an opportunity for the caller to fully configure this behavior without
  // making the LScript core too opinionated about which unit encodings it
  // supports.
  //
  // This map is used for a number of different use cases:
  // - Collapse semantically identical names for each unit to a single canonical
  //   string representation, which simplifies our unit-handling code later on
  // - In some cases where a string representation of a unit is ambiguous (I'm
  //   looking at you, "oz"), gives callers enough rope to decide what the
  //   strings in their data actually mean.
  //
  // The set of values is the set of Units this quantity can take on (just like
  // the `units: Array<Unit>` on a QuantityType)
  encodedValuesToUnits: Map<string, Unit>;
};

export type RowExprType =
  | StringType
  | BooleanType
  | NumberType
  | RowQuantityType
  | DateType
  | DatetimeType
  | NullableType
  // enable struct-like types such as `tags` for custom columns
  // TODO [CPT-6941]: remove this type once custom columns are directly available in the input type
  | RowStructType;

export type RowType = Map<string, RowExprType>;

// TODO [CPT-6941]: remove this function and it's usages once custom columns are
// directly available in the input type
export function assertNonStructColumnType(
  ty: RowExprType
): asserts ty is Exclude<RowExprType, RowStructType> {
  if (ty.ty === 'struct') {
    throw new Error('Struct type not allowed');
  }
}
